import React from "react";
import { makeStyles } from "@material-ui/core/styles";
import {
  ExpansionPanel,
  ExpansionPanelSummary,
  ExpansionPanelDetails,
  Typography,
  Box,
} from "@material-ui/core";
import {
  ExpandMore as ExpandMoreIcon,
  Help as HelpIcon,
  AddCircleOutline as AddCircleOutlineIcon,
  PlayArrow as PlayArrowIcon,
  PersonOutline as PersonOutlineIcon,
  CheckCircleOutline as CheckCircleOutlineIcon,
  Lock as LockIcon,
  Search as SearchIcon,
  Block as BlockIcon,
} from "@material-ui/icons";

const useStyles = makeStyles((theme) => ({
  faqContainer: {
    padding: theme.spacing(2),
  },
  faqTitle: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(3),
    color: "#F97B18",
    fontWeight: 600,
  },
  expansionPanel: {
    marginBottom: theme.spacing(1),
    borderRadius: "8px",
    "&:before": {
      display: "none",
    },
    "&.Mui-expanded": {
      margin: theme.spacing(1, 0),
    },
    boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
  },
  expansionPanelSummary: {
    backgroundColor: "#f8f9fa",
    borderRadius: "8px",
    minHeight: 56,
    "&.Mui-expanded": {
      minHeight: 56,
      borderBottomLeftRadius: 0,
      borderBottomRightRadius: 0,
    },
    "&:hover": {
      backgroundColor: "#e9ecef",
    },
  },
  expansionPanelDetails: {
    padding: theme.spacing(2, 3),
    backgroundColor: "#ffffff",
    borderTop: "1px solid #e9ecef",
    borderRadius: "0 0 8px 8px",
  },
  statusTitle: {
    fontWeight: 600,
    color: "#2c3e50",
    fontSize: "1rem",
  },
  statusDescription: {
    color: "#6c757d",
    lineHeight: 1.8,
    marginTop: theme.spacing(1),
  },
  statusIcon: {
    marginRight: theme.spacing(1),
    color: "#F97B18",
  },
  statusHeader: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
  },
  statusIconContainer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: 40,
    height: 40,
    borderRadius: "50%",
    marginRight: theme.spacing(1),
  },
  infoBox: {
    backgroundColor: "#f0f7ff",
    borderLeft: "4px solid #F97B18",
    padding: theme.spacing(1.5),
    marginTop: theme.spacing(2),
    borderRadius: "4px",
  },
  infoTitle: {
    fontWeight: 600,
    color: "#2c3e50",
    marginBottom: theme.spacing(0.5),
    fontSize: "0.9rem",
  },
  infoText: {
    color: "#495057",
    fontSize: "0.875rem",
    lineHeight: 1.6,
  },
  exampleBox: {
    backgroundColor: "#fff9e6",
    borderLeft: "4px solid #ffc107",
    padding: theme.spacing(1.5),
    marginTop: theme.spacing(1.5),
    borderRadius: "4px",
  },
  exampleTitle: {
    fontWeight: 600,
    color: "#856404",
    marginBottom: theme.spacing(0.5),
    fontSize: "0.9rem",
  },
  exampleText: {
    color: "#856404",
    fontSize: "0.875rem",
    lineHeight: 1.6,
  },
}));

const statusData = [
  {
    title: "Abertos",
    icon: AddCircleOutlineIcon,
    iconColor: "#28a745",
    description: "Seu chamado foi criado com sucesso e está na fila de atendimento.",
    whatHappens: "Nossa equipe ainda não começou a trabalhar no seu chamado. Ele está aguardando para ser analisado e atribuído a um técnico.",
    whatToDo: "Aguarde! Nossa equipe irá analisar seu chamado em breve. Você receberá atualizações por e-mail quando houver mudanças.",
    example: "É como se você tivesse enviado um e-mail e ele estivesse na caixa de entrada aguardando leitura.",
  },
  {
    title: "Em Progresso",
    icon: PlayArrowIcon,
    iconColor: "#007bff",
    description: "Nossa equipe está trabalhando ativamente na resolução do seu problema.",
    whatHappens: "Um técnico já está analisando e trabalhando na solução do seu chamado. Pode estar investigando o problema, testando soluções ou implementando correções.",
    whatToDo: "Aguarde enquanto trabalhamos na solução. Se precisar de mais informações, nossa equipe entrará em contato. Você pode acompanhar o progresso pelos comentários no chamado.",
    example: "É como se um mecânico estivesse com seu carro na oficina, já com as ferramentas na mão, trabalhando para resolver o problema.",
  },
  {
    title: "Aguardando Usuário",
    icon: PersonOutlineIcon,
    iconColor: "#ffc107",
    description: "Precisamos de informações ou ações da sua parte para continuar.",
    whatHappens: "Nossa equipe precisa de algo que só você pode fornecer, como: mais detalhes sobre o problema, acesso a alguma conta, confirmação de algum dado, ou você precisa testar algo que enviamos.",
    whatToDo: "Verifique os comentários no chamado! Lá você encontrará exatamente o que precisamos. Responda o mais rápido possível para que possamos continuar.",
    example: "É como se o técnico perguntasse: 'Qual é a senha da sua conta?' e estivesse esperando sua resposta para continuar.",
  },
  {
    title: "Validação do Usuário",
    icon: CheckCircleOutlineIcon,
    iconColor: "#17a2b8",
    description: "A solução foi implementada! Agora precisamos que você confirme se está tudo funcionando.",
    whatHappens: "Nossa equipe já fez a correção ou atendeu sua solicitação. Agora precisamos que você teste e confirme se o problema foi resolvido.",
    whatToDo: "Teste a solução que implementamos! Se estiver tudo certo, confirme no chamado. Se ainda houver problemas, informe detalhadamente o que não está funcionando.",
    example: "É como se o técnico dissesse: 'Corrigi o problema, pode testar agora?' e estivesse esperando você confirmar se está tudo ok.",
  },
  {
    title: "Fechados",
    icon: LockIcon,
    iconColor: "#6c757d",
    description: "O chamado foi finalizado e encerrado.",
    whatHappens: "O chamado foi encerrado porque você validou que a solução funcionou, ou porque passaram 7 dias sem nenhuma interação (resposta automática).",
    whatToDo: "Se o problema voltar ou surgir algo relacionado, você pode criar um novo chamado. O histórico deste chamado permanece disponível para consulta.",
    example: "É como fechar um ticket de cinema após assistir ao filme. O problema foi resolvido e o caso está arquivado.",
  },
  {
    title: "Bloqueado",
    icon: BlockIcon,
    iconColor: "#dc3545",
    description: "Sua solicitação foi registrada, mas não será implementada no momento.",
    whatHappens: "Chamados de melhorias, sugestões ou funcionalidades novas que não estão no planejamento atual. Eles ficam guardados no backlog (lista de pendências) para serem analisados no futuro.",
    whatToDo: "Sua ideia foi registrada! Nossa equipe de produto vai analisar no futuro. Se for priorizada, você será notificado. Por enquanto, continue usando o sistema normalmente.",
    example: "É como sugerir uma nova cor para a parede da sala. A sugestão foi anotada, mas não será pintada agora. Pode ser considerada em uma reforma futura.",
  },
  {
    title: "Análise",
    icon: SearchIcon,
    iconColor: "#6f42c1",
    description: "Estamos investigando o problema em profundidade antes de aplicar uma solução.",
    whatHappens: "O problema é mais complexo e requer uma investigação detalhada. Nossa equipe está analisando logs, testando cenários, verificando impactos e estudando a melhor forma de resolver sem causar outros problemas.",
    whatToDo: "Aguarde com paciência. Problemas complexos levam mais tempo, mas garantimos uma solução mais robusta. Você será atualizado conforme avançamos na investigação.",
    example: "É como um médico fazendo vários exames antes de dar um diagnóstico. Precisa entender completamente o problema antes de tratar.",
  },
];

// Função helper para obter a descrição do status
export const getStatusDescription = (statusText) => {
  if (!statusText) return null;
  
  // Normalizar o texto do status
  const normalizedStatus = String(statusText).toLowerCase()
    .replace(/_/g, ' ')
    .replace(/-/g, ' ')
    .trim();
  
  // Mapeamento de status para índices do statusData
  const statusMap = {
    'aberto': 0,
    'abertos': 0,
    'open': 0,
    'em progresso': 1,
    'progress': 1,
    'in progress': 1,
    'aguardando usuário': 2,
    'aguardando usuario': 2,
    'aguardando': 2,
    'waiting': 2,
    'validação do usuário': 3,
    'validacao do usuario': 3,
    'validação': 3,
    'validacao': 3,
    'validation': 3,
    'fechado': 4,
    'fechados': 4,
    'closed': 4,
    'bloqueado': 4,
    'blocked': 5,
    'análise': 6,
    'analise': 6,
    'analysis': 6,
  };
  
  // Buscar correspondência exata ou parcial
  for (const [key, index] of Object.entries(statusMap)) {
    if (normalizedStatus.includes(key) || key.includes(normalizedStatus)) {
      const statusInfo = statusData[index];
      return statusInfo ? statusInfo.description : null;
    }
  }
  
  // Tentar buscar por palavras-chave parciais
  if (normalizedStatus.includes('abert')) return statusData[0].description;
  if (normalizedStatus.includes('progress')) return statusData[1].description;
  if (normalizedStatus.includes('aguard') || normalizedStatus.includes('wait')) return statusData[2].description;
  if (normalizedStatus.includes('valid')) return statusData[3].description;
  if (normalizedStatus.includes('fech') || normalizedStatus.includes('close')) return statusData[4].description;
  if (normalizedStatus.includes('bloque') || normalizedStatus.includes('block')) return statusData[5].description;
  if (normalizedStatus.includes('analis') || normalizedStatus.includes('analysis')) return statusData[6].description;
  
  return null;
};

function FAQ() {
  const classes = useStyles();

  return (
    <Box className={classes.faqContainer}>
      <Typography variant="h5" className={classes.faqTitle}>
        <HelpIcon className={classes.statusIcon} />
        Perguntas Frequentes - Status dos Chamados
      </Typography>
      <Typography
        variant="body2"
        color="textSecondary"
        style={{ marginBottom: 24 }}
      >
        Entenda o que significa cada status do seu chamado de suporte:
      </Typography>

      {statusData.map((status, index) => {
        const StatusIcon = status.icon;
        return (
          <ExpansionPanel
            key={index}
            className={classes.expansionPanel}
            defaultExpanded={index === 0}
          >
            <ExpansionPanelSummary
              expandIcon={<ExpandMoreIcon style={{ color: "#F97B18" }} />}
              className={classes.expansionPanelSummary}
            >
              <div className={classes.statusHeader}>
                <div
                  className={classes.statusIconContainer}
                  style={{ backgroundColor: `${status.iconColor}20` }}
                >
                  <StatusIcon style={{ color: status.iconColor, fontSize: 24 }} />
                </div>
                <Typography className={classes.statusTitle}>
                  {status.title}
                </Typography>
              </div>
            </ExpansionPanelSummary>
            <ExpansionPanelDetails className={classes.expansionPanelDetails}>
              <Box>
                <Typography
                  className={classes.statusDescription}
                  style={{ fontWeight: 500, marginBottom: 12 }}
                >
                  {status.description}
                </Typography>

                <Box className={classes.infoBox}>
                  <Typography className={classes.infoTitle}>
                    📋 O que está acontecendo?
                  </Typography>
                  <Typography className={classes.infoText}>
                    {status.whatHappens}
                  </Typography>
                </Box>

                <Box className={classes.infoBox} style={{ backgroundColor: "#e8f5e9", borderLeftColor: "#28a745" }}>
                  <Typography className={classes.infoTitle} style={{ color: "#2e7d32" }}>
                    ✅ O que você deve fazer?
                  </Typography>
                  <Typography className={classes.infoText} style={{ color: "#2e7d32" }}>
                    {status.whatToDo}
                  </Typography>
                </Box>

                <Box className={classes.exampleBox}>
                  <Typography className={classes.exampleTitle}>
                    💡 Exemplo prático:
                  </Typography>
                  <Typography className={classes.exampleText}>
                    {status.example}
                  </Typography>
                </Box>
              </Box>
            </ExpansionPanelDetails>
          </ExpansionPanel>
        );
      })}
    </Box>
  );
}

export default FAQ;

